import cx from 'classnames';
import { Component, createRef } from 'react';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ELSIcon } from 'components/common/els';

export interface ConsentForBloodTransfusionSelectProps {
  chartFormField: ChartMetaFormField;
  placeholder: string;
}

interface ConsentForBloodTransfusionSelectState {
  activeIndex: number;
  isOpen: boolean;
}

class ConsentForBloodTransfusionSelect extends Component<ConsentForBloodTransfusionSelectProps, ConsentForBloodTransfusionSelectState> {
  static displayName = 'ConsentForBloodTransfusionSelect';

  containerRef = createRef<HTMLDivElement>();

  constructor(props: ConsentForBloodTransfusionSelectProps) {
    super(props);
    this.state = {
      activeIndex: -1,
      isOpen: false
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideMouseDown);
  }

  handleOutsideMouseDown = (event: MouseEvent) => {
    if (this.containerRef.current && !this.containerRef.current.contains(event.target as Node)) {
      this.closeDropdown();
    }
  };

  getOptions = (): ContentItem[] =>
    this.props.chartFormField.chartContent?.filter(
      (item) => !item.isHidden && (item.isActive || item.isActive === undefined) && !(item as ContentItem & { hidden?: boolean }).hidden
    ) ?? [];

  getSelectedOption = (): ContentItem | undefined => {
    const value = String(this.props.chartFormField.value ?? '');
    if (!value) return undefined;
    return this.getOptions().find((item) => item.value === value);
  };

  getDisplayValue = (): string => this.getSelectedOption()?.label || this.props.placeholder;

  getActiveIndex = (): number => {
    const selectedOption = this.getSelectedOption();
    const selectedIndex = selectedOption ? this.getOptions().findIndex((item) => item.id === selectedOption.id) : -1;
    return selectedIndex >= 0 ? selectedIndex : 0;
  };

  openDropdown = () => {
    if (this.props.chartFormField.disabled) return;
    this.setState({ activeIndex: this.getActiveIndex(), isOpen: true });
  };

  closeDropdown = () => {
    if (this.state.isOpen && this.props.chartFormField.onBlur) {
      this.props.chartFormField.onBlur(this.props.chartFormField);
    }
    this.setState({ activeIndex: -1, isOpen: false });
  };

  toggleDropdown = () => {
    if (this.state.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  };

  selectOption = (option: ContentItem | undefined) => {
    if (!option || option.disabled) return;
    if (this.props.chartFormField.onChange) {
      this.props.chartFormField.onChange(this.props.chartFormField, { target: { value: option.value } });
    }
    this.closeDropdown();
  };

  moveActiveIndex = (step: number) => {
    const options = this.getOptions();
    if (!options.length) return;
    this.setState((prevState) => {
      const currentIndex = prevState.activeIndex >= 0 ? prevState.activeIndex : this.getActiveIndex();
      return { activeIndex: (currentIndex + step + options.length) % options.length, isOpen: true };
    });
  };

  handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.state.isOpen) {
          this.moveActiveIndex(1);
        } else {
          this.openDropdown();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.state.isOpen) {
          this.moveActiveIndex(-1);
        } else {
          this.openDropdown();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.state.isOpen) {
          this.selectOption(this.getOptions()[this.state.activeIndex]);
        } else {
          this.openDropdown();
        }
        break;
      case 'Escape':
        this.closeDropdown();
        break;
      default:
        break;
    }
  };

  render() {
    const { chartFormField, placeholder } = this.props;
    const { activeIndex, isOpen } = this.state;
    const { disabled, name } = chartFormField;
    const options = this.getOptions();
    const selectedOption = this.getSelectedOption();
    const hasValue = !!selectedOption;
    const listboxId = `${name}-listbox`;

    return (
      <div className={cx('consent-blood-transfusion-select', name)} ref={this.containerRef}>
        <button
          type="button"
          id={name}
          className={cx('consent-blood-transfusion-select__trigger', {
            'consent-blood-transfusion-select__trigger--open': isOpen,
            'consent-blood-transfusion-select__trigger--disabled': disabled
          })}
          onClick={this.toggleDropdown}
          onKeyDown={this.handleTriggerKeyDown}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-label={chartFormField.label || placeholder}
        >
          <span
            className={cx('consent-blood-transfusion-select__value', {
              'consent-blood-transfusion-select__value--placeholder': !hasValue
            })}
          >
            {this.getDisplayValue()}
          </span>
          <ELSIcon prefix="hmds" name="chevron-down" direction="right" size="1x1o2" />
        </button>

        {isOpen && (
          <div id={listboxId} role="listbox" className="consent-blood-transfusion-select__menu" aria-label={chartFormField.label || placeholder}>
            {options.map((option, index) => (
              <button
                type="button"
                key={option.id || option.value || option.label}
                role="option"
                className={cx('consent-blood-transfusion-select__option', {
                  'consent-blood-transfusion-select__option--active': activeIndex === index,
                  'consent-blood-transfusion-select__option--clear': option.value === ''
                })}
                disabled={option.disabled}
                aria-selected={hasValue && selectedOption?.id === option.id}
                onMouseEnter={() => this.setState({ activeIndex: index })}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => this.selectOption(option)}
              >
                {option.label || option.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default ConsentForBloodTransfusionSelect;
